import { Component, DOCUMENT, ElementRef, Input, Renderer2, ViewChild, inject, output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { UserSession } from "src/app/core/models/user.model";
import { PlatformService } from "src/app/core/services/platform/platform.service";
import { UserService } from "src/app/core/services/user/user.service";
import { Category } from "src/app/shared/services/common-api.service";
import { HeaderData, SearchSuggestionItem } from "../category/header-data";
import { DeviceType } from "../dynamic-header.component";
import { MenuCollapseService } from "../services/menu-collapse.service";


@Component({
    selector: 'common-header',
    template: '',
    standalone: false
})

export class CommonHeaderFeature {
    protected renderer = inject(Renderer2);
    protected menuCollapseService = inject(MenuCollapseService);
    protected elementRef = inject(ElementRef);
    protected platformService = inject(PlatformService);
    protected document = inject<Document>(DOCUMENT);
    protected userService = inject(UserService);

    @ViewChild('changeHeaderElement') changeHeaderElement!: ElementRef;

    @ViewChild('dynamicCategoryHeader') dynamicCategoryHeader!: ElementRef;
    @ViewChild('changeHeaderMobileElement') changeHeaderMobileElement!: ElementRef;
    @ViewChild('dynamicCategoryHeaderMobile') dynamicCategoryHeaderMobile!: ElementRef;
    isToggled = false;
    showMobileSearchPanel = false;
    isHamburgerMenu = false;
    showActiveColor = false;
    isSearchOverlay = false;
    currentMenuId = "";
    showSubcategoryPanel = false;
    mobileSubCategory!: Category[];
    user: UserSession = this.userService.user();

    device = DeviceType;
    isBrowser = false;
    searchInputControl = new FormControl<string | null>('');
    private menuListenersAttached = false; // ✅ Prevent duplicate event listeners


    @Input() set headerData(value: HeaderData) {
        this._headerData = value;
        this.addHoverForMenu();
    }
    get headerData() {
        return this._headerData;
    }
    protected _headerData!: HeaderData;

    @Input() set selectedDeviceType(value: string) {
        this._selectedDeviceType = value;
        if (value === this.device.desktop) {
            this.isHamburgerMenu = false;
            this.menuCollapseService.setHamburgerMenu(false);
            this.showMobileSearchPanel = false;
        }
        this.repositionHeaderMenu();
    };
    get selectedDeviceType() {
        return this._selectedDeviceType;
    }
    protected _selectedDeviceType = "";

    // Update output to use Angular 20 output() function
    searchQuery$ = output<{ keyboardEvent: KeyboardEvent, suggestionItem: SearchSuggestionItem, searchInputValue?: string }>();


    constructor() {
        this.isBrowser = this.platformService.isBrowser();
    }

    // Method to refresh user data (can be called by child classes)
    protected refreshUserData(): void {
        this.user = this.userService.user();
    }

    // Getter for current user data
    get currentUser(): UserSession {
        return this.userService.user();
    }

    removeHoverStyle() {
        this.showActiveColor = false;
        setTimeout(() => {
            const menuItem = document.getElementsByClassName('hover-color-inactive');
            if (menuItem.length > 0) {
                for (let i = 0; i < menuItem.length; i++) {
                    this.renderer.removeStyle(menuItem[i], 'color'); // Remove text color
                    this.renderer.removeStyle(menuItem[i], 'background-color'); // Remove background color
                }
            }

            this.currentMenuId = "";
        }, 0);
    }

    addHoverForMenu() {
        // ✅ Prevent duplicate event listener attachments
        if (this.isBrowser && !this.menuListenersAttached) {
            const menuElements = this.elementRef.nativeElement.querySelectorAll('.hoverMenu');
            const subMenuElements = this.elementRef.nativeElement.querySelectorAll('.hoverSubMenu');

            // Only attach if we have menu elements
            if (menuElements.length > 0 || subMenuElements.length > 0) {
                menuElements?.forEach((element: HTMLElement) => {
                    element.addEventListener('mouseover', this.handleMenuMouseOver.bind(this));
                    element.addEventListener('mouseout', this.handleMenuMouseOut.bind(this));
                });
                subMenuElements?.forEach((element: HTMLElement) => {
                    element.addEventListener('mouseover', this.handleSubMenuMouseOver.bind(this));
                    element.addEventListener('mouseout', this.handleSubMenuMouseOut.bind(this));
                });

                this.menuListenersAttached = true; // ✅ Mark as attached

                if (!this.headerData.searchIconMobile.isVisibleOnMobile) {
                    this.showMobileSearchPanel = true;
                }
            }
        }
    }

    handleSubMenuMouseOver(event: MouseEvent): void {

        const subMenus = document.getElementsByClassName("hide-sub-menu-header");

        Array.from(subMenus).forEach(subMenu => {
            this.renderer.removeStyle(subMenu, 'display');
        });
        const targetElement = event.target as HTMLElement;
        if (this.selectedDeviceType === "desktop") {
            this.renderer.setStyle(targetElement, 'color', this.headerData.desktopStyleConfiguration.subMenuHoverTextColor);
            this.renderer.setStyle(targetElement, 'background-color', this.headerData.desktopStyleConfiguration.subMenuHoverBackgroundColor);
        } else if (this.selectedDeviceType === "mobile") {
            this.renderer.setStyle(targetElement, 'color', this.headerData.mobileStyleConfiguration.subMenuHoverTextColor);
            this.renderer.setStyle(targetElement, 'background-color', this.headerData.mobileStyleConfiguration.subMenuHoverBackgroundColor);
        }
    }

    handleSubMenuMouseOut(event: MouseEvent): void {
        // Handle mouseout event
        const targetElement = event.target as HTMLElement;
        this.renderer.removeStyle(targetElement, 'color'); // Remove text color
        this.renderer.removeStyle(targetElement, 'background-color'); // Remove background color
    }

    handleMenuMouseOver(event: MouseEvent): void {
        const subMenus = document.getElementsByClassName("hide-sub-menu-header");

        Array.from(subMenus).forEach(subMenu => {
            this.renderer.removeStyle(subMenu, 'display');
        });
        const targetElement = event.target as HTMLElement;
        if (this.selectedDeviceType === "desktop") {
            this.renderer.setStyle(targetElement, 'color', this.headerData.desktopStyleConfiguration.menuHoverTextColor);
            this.renderer.setStyle(targetElement, 'background-color', this.headerData.desktopStyleConfiguration.menuHoverBackgroundColor);
        } else if (this.selectedDeviceType === "mobile") {
            this.renderer.setStyle(targetElement, 'color', this.headerData.mobileStyleConfiguration.menuHoverTextColor);
            this.renderer.setStyle(targetElement, 'background-color', this.headerData.mobileStyleConfiguration.menuHoverBackgroundColor);
        }
    }

    handleMenuMouseOut(event: MouseEvent): void {
        // Handle mouseout event
        const targetElement = event.target as HTMLElement;
        this.renderer.removeStyle(targetElement, 'color'); // Remove text color
        this.renderer.removeStyle(targetElement, 'background-color'); // Remove background color

        setTimeout(() => {
            const menuItemToActivate = document.getElementsByClassName('hover-color-active');
            if (menuItemToActivate && menuItemToActivate.length > 0) {
                for (let i = 0; i < menuItemToActivate.length; i++) {
                    this.renderer.setStyle(menuItemToActivate[i], 'color', this.headerData.desktopStyleConfiguration.menuHoverTextColor);
                    this.renderer.setStyle(menuItemToActivate[i], 'background-color', this.headerData.desktopStyleConfiguration.menuHoverBackgroundColor);
                }
            }

            const menuItemToDeActivate = document.getElementsByClassName('hover-color-inactive');
            if (menuItemToDeActivate.length > 0) {
                for (let i = 0; i < menuItemToDeActivate.length; i++) {
                    this.renderer.removeStyle(menuItemToDeActivate[i], 'color'); // Remove text color
                    this.renderer.removeStyle(menuItemToDeActivate[i], 'background-color'); // Remove background color

                }
            }
        }, 10);
    }

    repositionHeaderMenu() {
        if (this.isBrowser && this.headerData?.menuItems) {
            const categoryHeaderIndex = this.headerData.menuItems.findIndex(item => item.text === "Category Header");
            if (categoryHeaderIndex !== -1) {
                const parentUl = this.changeHeaderElement?.nativeElement.querySelector('.mega-menu-category ul');
                const parentUlMobile = this.changeHeaderMobileElement?.nativeElement.querySelector('.mobile-menu-list ul');

                if (parentUl) {
                    const liElements = parentUl.querySelectorAll('.static-menu');

                    // Insert dynamicCategoryHeader at the index of "Category Header" plus one
                    if (this.dynamicCategoryHeader?.nativeElement) {
                        if (liElements.length > categoryHeaderIndex) {
                            parentUl.insertBefore(this.dynamicCategoryHeader?.nativeElement, liElements[categoryHeaderIndex]);
                        } else {
                            parentUl.appendChild(this.dynamicCategoryHeader?.nativeElement);
                        }
                    }
                }
                if (parentUlMobile) {
                    const liElementsMobile = parentUlMobile.querySelectorAll('.static-menu-mobile');
                    // Insert dynamicCategoryHeader at the index of "Category Header" plus one
                    if (this.dynamicCategoryHeaderMobile?.nativeElement) {
                        if (parentUlMobile.length > categoryHeaderIndex) {
                            parentUlMobile.insertBefore(this.dynamicCategoryHeaderMobile?.nativeElement, liElementsMobile[categoryHeaderIndex]);
                        } else {
                            parentUlMobile.appendChild(this.dynamicCategoryHeader?.nativeElement);
                        }
                    }
                }
            }
            this.addHoverForMenu();
        }
    }

    toggleHamburgerMenu() {
        this.menuCollapseService.toggleHamburgerMenu();
        this.isHamburgerMenu = !this.isHamburgerMenu;
        if (this.menuCollapseService.isHamburgerMenu()) {
            this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        } else {
            this.renderer.removeStyle(this.document.body, 'overflow');
        }
    }

    toggleSearchDropDown() {
        this.showMobileSearchPanel = !this.showMobileSearchPanel;
        this.isHamburgerMenu = false;
    }


    applyFocusEvent() {
        const inputElements = this.elementRef.nativeElement.querySelectorAll('.search-input');
        let isMouseUser = false;

        // Detect mouse users by listening to the mousedown event
        this.renderer.listen('window', 'mousedown', () => {
            isMouseUser = true;
        });

        // Detect keyboard users by listening to the keydown event
        this.renderer.listen('window', 'keydown', () => {
            isMouseUser = false;  // If a key is pressed, it means they are not a mouse user
        });

        inputElements.forEach((element: HTMLElement) => {
            // Focus event handler
            this.renderer.listen(element, 'focusin', () => {
                if (isMouseUser) {
                    // Apply styles for mouse click focus
                    this.renderer.setStyle(element, 'border-color', this.headerData.desktopStyleConfiguration.searchInputBorderColor);
                } else {
                    // Apply styles for keyboard focus
                    this.renderer.setStyle(element, 'border-color', this.headerData.desktopStyleConfiguration.searchInputBorderColor);
                }
            });

            // Blur event handler (reset border)
            this.renderer.listen(element, 'focusout', () => {
                this.renderer.removeStyle(element, 'border');
            });
        });
    }

    searchIconClick() {
        const event = new KeyboardEvent('keyup', {
            key: 'Enter',
        });

        // Dispatch the event to the search input element
        const inputElement = document.querySelector('input[type="search"]');
        if (inputElement) {
            inputElement.dispatchEvent(event);
        }
    }

    disableSearchPanel() {
        this.showMobileSearchPanel = false;
        this.isSearchOverlay = false;
        this.renderer.removeStyle(this.document.body, 'overflow');
        // Removed searchInputControl.reset() - let user clear input manually
    }

    searchProduct(searchQuery: { keyboardEvent: KeyboardEvent, suggestionItem: SearchSuggestionItem }): void {
        const { keyboardEvent, suggestionItem } = searchQuery;

        if (suggestionItem) {
            this.searchQuery$.emit(searchQuery);
            this.disableSearchPanel();

        } else if (keyboardEvent) {
            if (keyboardEvent.key === 'Enter') {
                const inputValue = this.searchInputControl?.value || '';
                this.searchQuery$.emit({ ...searchQuery, searchInputValue: inputValue });
                this.disableSearchPanel();
            }
        }
    }
}